import Grid from '@mui/material/Grid';
import { Box, CircularProgress, Divider, IconButton, Modal, Paper, Stack, SvgIcon, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import 'dayjs/locale/de';
import dayjs from 'dayjs';
import PageHeader from '../../common/Components/PageHeader';
import BoxHeader from '../../common/Components/BoxHeader';
import { useFetch, Status } from '../../hooks/useFetch';
import { UserFeedback, UserFeedbackExtended } from '@aviation/cabinapp-userfeedbackservice-sdk';
import FeedbackImage from './FeedbackImage';
import { ReactComponent as BugIcon } from '../../icons/bug.svg';
import { ReactComponent as IdeaIcon } from '../../icons/light-bulb.svg';
import { ReactComponent as CloseIcon } from '../../icons/cross.svg';
import { USERFEEDBACK } from '../../common/paths';

const isIdea = (type?: string) => type?.toLowerCase() === 'idea';

const typeLabel = (type?: string) => isIdea(type) ? 'Idea/ Feature Request' : 'Bug/ Issue';

interface IDetailRowProps {
    label: string;
    value?: string;
}

function DetailRow({ label, value }: IDetailRowProps) {
    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', py: 1.5 }}>
                <Typography variant='body2' color='text.secondary'>{label}</Typography>
                <Typography variant='body2' sx={{ fontWeight: 500, textAlign: 'right' }}>{value || '-'}</Typography>
            </Box>
            <Divider />
        </>
    );
}

function SimilarReportCard({ item }: { item: UserFeedback }) {
    const navigate = useNavigate();
    const TypeIcon = isIdea(item.Type) ? IdeaIcon : BugIcon;
    const clickable = !!item.Id;

    return (
        <Paper
            elevation={2}
            onClick={() => clickable && navigate(`/${USERFEEDBACK}/${encodeURIComponent(item.Id!)}`)}
            sx={{ p: 2, mb: 2, cursor: clickable ? 'pointer' : 'default' }}
        >
            <Grid container spacing={2} alignItems='center'>
                <Grid item xs='auto'>
                    <SvgIcon component={TypeIcon} inheritViewBox color='primary' />
                </Grid>
                <Grid item xs={12} md>
                    <Typography
                        variant='body2'
                        sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                        {item.Content}
                    </Typography>
                </Grid>
                <Grid item xs={12} md='auto'>
                    <Stack direction='row' spacing={4}>
                        <Box>
                            <Typography variant='caption' color='text.secondary' display='block'>3LC</Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>{item.LtrCode || '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant='caption' color='text.secondary' display='block'>Client Code</Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>{item.ClientCode || '-'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant='caption' color='text.secondary' display='block'>App Version</Typography>
                            <Typography variant='body2' sx={{ fontWeight: 600 }}>{item.AppVer || '-'}</Typography>
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
}

function UserFeedbackDetails() {
    const { id } = useParams();
    const { status, data = {} as UserFeedbackExtended } = useFetch<UserFeedbackExtended>(`/api/userfeedback/byid/${id}`);

    const [previewSrc, setPreviewSrc] = useState<string | undefined>(undefined);

    const TypeIcon = isIdea(data.Type) ? IdeaIcon : BugIcon;
    const files = data.Files ?? [];
    const similarReports = data.SimilarReports ?? [];

    const handleImageClick = (src: string) => {
        setPreviewSrc(src);
    };

    const handleClosePreview = () => {
        setPreviewSrc(undefined);
    };

    const createdOn = useMemo(() => data.CreatedOn ? dayjs(data.CreatedOn).format('DD.MM.YYYY HH:mm') : undefined, [data.CreatedOn]);
    const insertedOn = useMemo(() => data.InsertedOn ? dayjs(data.InsertedOn).format('DD.MM.YYYY HH:mm') : undefined, [data.InsertedOn]);

    const loading = status === Status.Idle || status === Status.Fetching;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <PageHeader title={typeLabel(data.Type)}>
                    <SvgIcon component={TypeIcon} inheritViewBox color='primary' sx={{ fontSize: 32 }} />
                </PageHeader>
            </Grid>

            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 2 }} elevation={3}>
                    <BoxHeader title='Report' />
                    <Box>
                        <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                            {data.Content}
                        </Typography>
                        {files.length > 0 && (
                            <Stack direction='row' spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 2 }}>
                                {files.map((f, idx) => (
                                    <FeedbackImage
                                        key={`${f.Url}-${idx}`}
                                        url={f.Url ?? ''}
                                        onClick={handleImageClick}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }} elevation={3}>
                    <BoxHeader title='Details' />
                    <Box>
                        <DetailRow label='3LC' value={data.LtrCode} />
                        <DetailRow label='Client Code' value={data.ClientCode} />
                        <DetailRow label='Mail' value={data.MailAdr} />
                        <DetailRow label='App Version' value={data.AppVer} />
                        <DetailRow label='Device' value={data.Device} />
                        <DetailRow label='Screen' value={data.Screen} />
                        <DetailRow label='Created on' value={createdOn} />
                        <DetailRow label='Inserted on' value={insertedOn} />
                    </Box>
                </Paper>
            </Grid>

            {(status === Status.Fetched && similarReports.length > 0) && (
                <Grid item xs={12}>
                    <Typography variant='subtitle1' component='h2' sx={{ textTransform: 'uppercase', mb: 2 }}>
                        Similar Reports
                    </Typography>
                    {similarReports.map((r, idx) => (
                        <SimilarReportCard key={r.Id ?? idx} item={r} />
                    ))}
                </Grid>
            )}

            <Modal
                open={Boolean(previewSrc)}
                onClose={handleClosePreview}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <Box sx={{ position: 'relative', outline: 'none', maxWidth: '90vw', maxHeight: '90vh' }}>
                    <IconButton
                        onClick={handleClosePreview}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            color: 'common.white',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                        }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox />
                    </IconButton>
                    {previewSrc && (
                        <Box
                            component='img'
                            src={previewSrc}
                            alt='preview'
                            sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block', borderRadius: 1 }}
                        />
                    )}
                </Box>
            </Modal>
        </Grid>
    );
}

export default UserFeedbackDetails;
